import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';

import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { ErknmSubjectInfo, KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmErknmOrganizationTemplateSelectOption } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmOrganizationTemplateSelectOption';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getOrganizationRafpNzaLabelBlockId,
  getOrganizationKppLabelBlockId,
  getOrganizationOkvedsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { KnmSonkoField } from 'KnmPackage/MainModule/components/KnmSonkoField';
import { getOkvedBlockId } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { ErknmRegistryCheck } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmRegistryCheck';

import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { KnmSubjectItInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectItInfo';
import { IKnmSubject248ItAccreditationResponse } from '../../../APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { KnmOkveds } from '../../../KnmPackage/MainModule/components/KnmOkveds';
import { KnmSubjectNkoInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectNkoInfo';
import { MspCodesEnum } from '../../../APIPackage/KnmModule/enums/MspCodesEnum';
import styles from './ErknmSubjectAdd.module.scss';
import { KnmMspField } from '../../../KnmPackage/MainModule/components/KnmMspField';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';

interface IErknmSubjectAddProps {
  id: string;
  name: string | undefined;
  watchOnly: boolean;
  kindKnmCode: KnmKindCodesEnum | undefined;
  organizationsTemplatesSelectOptions: IKnmErknmOrganizationTemplateSelectOption[] | undefined;
  organizationTemplate: IKnmErknmOrganizationTemplateSelectOption | undefined;
  organizationTemplateBlockId: string;
  inn: string | undefined;
  innLabelBlockId: string;
  ogrn: string | undefined;
  ogrnLabelBlockId: string;
  firstOrganizationName: string | undefined;
  organizationNameBlockId: string;
  checkSubjectResult: IKnmCheckSubjectResult | null;
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  kpp: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  rafpNza: string | undefined;
  isSonko: boolean | undefined;
  snils: string | undefined;
  ervkId: string | undefined;
  isPresentInItRegistry: boolean | undefined;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  smevItAccreditationRequestStatus: string | undefined;
  okveds: IKnmErknmFormSubject['okveds'] | undefined;
  mspCode: MspCodesEnum | undefined;
  mspRegistryDate: IKnmErknmFormSubject['mspRegistryDate'] | undefined;
  hideTitle?: boolean;
  isShowSubjectInfoNow?: boolean;
}

interface IErknmSubjectAddStoreProps {
  formRef: IKnmFormRef | undefined;
}

const ErknmSubjectAddWithoutStore = ({
  id,
  name,
  watchOnly,
  kindKnmCode,
  organizationsTemplatesSelectOptions,
  organizationTemplate,
  organizationTemplateBlockId,
  inn,
  innLabelBlockId,
  ogrn,
  ogrnLabelBlockId,
  firstOrganizationName,
  organizationNameBlockId,
  checkSubjectResult,
  isAutocomplete,
  formRef,
  subjectType,
  kpp,
  smevRequestStatus,
  rafpNza,
  isSonko,
  snils,
  ervkId,
  itAccreditationResponse,
  smevItAccreditationRequestStatus,
  isPresentInItRegistry,
  okveds,
  mspCode,
  mspRegistryDate,
  hideTitle,
  isShowSubjectInfoNow,
}: IErknmSubjectAddProps & IErknmSubjectAddStoreProps) => {
  const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
  const [isShowSubjectInfo, setIsShowSubjectInfo] = useState<boolean>(
    isShowSubjectInfoNow || kindKnmCode !== KnmKindCodesEnum.RO,
  );
  const [checkByInnMessage, setCheckByInnMessage] = useState<string | undefined>(undefined);
  const isNewOrganizationTemplate = organizationTemplate ? !organizationTemplate?.value : false;

  const organizationTemplateFieldName = `${name}.organizationTemplate` as keyof IKnmForm;
  const organizationFieldName = `${name}.organization` as keyof IKnmForm;
  const subjectTypeFieldName = `${name}.organization.subjectType` as keyof IKnmForm;
  const innFieldName = `${name}.organization.inn` as keyof IKnmForm;
  const ogrnFieldName = `${name}.organization.ogrn` as keyof IKnmForm;
  const organizationNameFieldName = `${name}.organization.organizationName` as keyof IKnmForm;
  const isFizFieldName = `${name}.organization.isFiz` as keyof IKnmForm;
  const kppFieldName = `${name}.organization.kpp` as keyof IKnmForm;
  const rafpNzaFieldName = `${name}.organization.rafpNza` as keyof IKnmForm;
  const isForeignSubjectAgencyNotRegistredInRfFieldName = `${name}.organization.isForeignSubjectAgencyNotRegistredInRf` as keyof IKnmForm;
  const countryFieldName = `${name}.organization.country` as keyof IKnmForm;
  const nkoForeignAgentRegistryNumberFieldName = `${name}.organization.nkoForeignAgentsRegistryNumber` as keyof IKnmForm;
  const nkoForeignAgentsRegistryInclusionDateFieldName = `${name}.organization.nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm;
  const nkoForeignAgentsRegistryExclusionDateFieldName = `${name}.organization.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm;
  const isNkoFieldName = `${name}.organization.isNko` as keyof IKnmForm;
  const isDocumentOrganizationFieldName = `${name}.organization.isDocumentOrganization` as keyof IKnmForm;
  const idFieldName = `${name}.organization.listId` as keyof IKnmForm;
  const guidFieldName = `${name}.organization.guid` as keyof IKnmForm;
  const locationAddressFieldName = `${name}.organization.locationAddress` as keyof IKnmForm;
  const workAddressFieldName = `${name}.organization.workAddress` as keyof IKnmForm;
  const factualAddressFieldName = `${name}.organization.factualAddress` as keyof IKnmForm;
  const snilsFieldName = `${name}.organization.snils` as keyof IKnmForm;
  const documentFieldName = `${name}.organization.document` as keyof IKnmForm;
  const sonkoFieldName = `${name}.organization.isSonko` as keyof IKnmForm;
  const mspCodeFieldName = `${name}.organization.mspCode` as keyof IKnmForm;
  const isPresentInItRegistryFieldName = `${name}.organization.isPresentInItRegistry` as keyof IKnmForm;
  const itAccreditationResponseFieldName = `${name}.organization.itAccreditationResponse` as keyof IKnmForm;
  const smevItAccreditationRequestStatusFieldName = `${name}.organization.smevItAccreditationRequestStatus` as keyof IKnmForm;
  const checkSubjectResultFieldName = `${name}.organization.checkSubjectResult` as keyof IKnmForm;

  const handleOrganizationTemplateSelectChange = useCallback(
    (value: IKnmErknmOrganizationTemplateSelectOption | undefined) => {
      const isNewTemplate = value && !value.value;

      if (!value && !isNewTemplate) {
        formRef?.current.change(organizationFieldName);
        setIsShowSubjectInfo(false);
      } else if (!value || isNewTemplate) {
        formRef?.current.change(subjectTypeFieldName);
        formRef?.current.change(innFieldName);
        formRef?.current.change(ogrnFieldName);
        formRef?.current.change(organizationNameFieldName);
        formRef?.current.change(isFizFieldName);
        formRef?.current.change(kppFieldName);
        formRef?.current.change(rafpNzaFieldName);
        formRef?.current.change(isForeignSubjectAgencyNotRegistredInRfFieldName);
        formRef?.current.change(countryFieldName);
        formRef?.current.change(nkoForeignAgentRegistryNumberFieldName);
        formRef?.current.change(nkoForeignAgentsRegistryInclusionDateFieldName);
        formRef?.current.change(nkoForeignAgentsRegistryExclusionDateFieldName);
        formRef?.current.change(isNkoFieldName);
        formRef?.current.change(guidFieldName);
        formRef?.current.change(locationAddressFieldName);
        formRef?.current.change(workAddressFieldName);
        formRef?.current.change(factualAddressFieldName);
        formRef?.current.change(snilsFieldName);
        formRef?.current.change(documentFieldName);
        formRef?.current.change(sonkoFieldName);
        formRef?.current.change(mspCodeFieldName);
        formRef?.current.change(isPresentInItRegistryFieldName);
        formRef?.current.change(itAccreditationResponseFieldName);
        formRef?.current.change(smevItAccreditationRequestStatusFieldName);
        formRef?.current.change(checkSubjectResultFieldName);
        if (!value) {
          formRef?.current.change(isDocumentOrganizationFieldName);
          formRef?.current.change(idFieldName);
        } else {
          formRef?.current.change(isDocumentOrganizationFieldName, true);
          formRef?.current.change(idFieldName, uuidv4());
        }
        setIsShowSubjectInfo(true);
      } else {
        formRef?.current.change(subjectTypeFieldName, value.subjectType);
        formRef?.current.change(innFieldName, value.inn);
        formRef?.current.change(ogrnFieldName, value.ogrn);
        formRef?.current.change(organizationNameFieldName, value.organizationName);
        formRef?.current.change(isFizFieldName, value.isFiz);
        formRef?.current.change(kppFieldName, value.kpp);
        formRef?.current.change(rafpNzaFieldName, value.rafpNza);
        formRef?.current.change(
          isForeignSubjectAgencyNotRegistredInRfFieldName,
          value.isForeignSubjectAgencyNotRegistredInRf,
        );
        formRef?.current.change(countryFieldName, value.country);
        formRef?.current.change(nkoForeignAgentRegistryNumberFieldName, value.nkoForeignAgentsRegistryNumber);
        formRef?.current.change(
          nkoForeignAgentsRegistryInclusionDateFieldName,
          value.nkoForeignAgentsRegistryInclusionDate,
        );
        formRef?.current.change(
          nkoForeignAgentsRegistryExclusionDateFieldName,
          value.nkoForeignAgentsRegistryExclusionDate,
        );
        formRef?.current.change(isNkoFieldName, !!value.nkoForeignAgentsRegistryInclusionDate);
        formRef?.current.change(isDocumentOrganizationFieldName, value.isDocumentOrganization);
        formRef?.current.change(idFieldName, value.id);
        formRef?.current.change(locationAddressFieldName, value.locationAddress);
        formRef?.current.change(workAddressFieldName, value.workAddress);
        formRef?.current.change(factualAddressFieldName, value.factualAddress);
        formRef?.current.change(snilsFieldName, value.snils);
        formRef?.current.change(documentFieldName, value.document);
        formRef?.current.change(sonkoFieldName, value.isSonko);
        formRef?.current.change(mspCodeFieldName, value.mspCode);
        formRef?.current.change(isPresentInItRegistryFieldName, value.isPresentInItRegistry);
        formRef?.current.change(itAccreditationResponseFieldName, value.itAccreditationResponse);
        formRef?.current.change(smevItAccreditationRequestStatusFieldName, value.smevItAccreditationRequestStatus);
        formRef?.current.change(checkSubjectResultFieldName, value.checkSubjectResult);
        setIsShowSubjectInfo(true);
      }
    },
    [
      formRef,
      guidFieldName,
      idFieldName,
      innFieldName,
      isDocumentOrganizationFieldName,
      isFizFieldName,
      ogrnFieldName,
      organizationNameFieldName,
      countryFieldName,
      isForeignSubjectAgencyNotRegistredInRfFieldName,
      kppFieldName,
      nkoForeignAgentRegistryNumberFieldName,
      nkoForeignAgentsRegistryInclusionDateFieldName,
      nkoForeignAgentsRegistryExclusionDateFieldName,
      isNkoFieldName,
      rafpNzaFieldName,
      subjectTypeFieldName,
    ],
  );

  const selectedOrganizationTemplateId = organizationTemplate?.id;
  const isOrganizationTemplateNotExist =
    !organizationTemplate?.isDocumentOrganization &&
    selectedOrganizationTemplateId &&
    !organizationsTemplatesSelectOptions?.some(selectOption => selectOption.id === selectedOrganizationTemplateId);

  useEffect(() => {
    if (isOrganizationTemplateNotExist) {
      formRef?.current.change(organizationTemplateFieldName);
      formRef?.current.change(innFieldName);
      formRef?.current.change(ogrnFieldName);
      formRef?.current.change(organizationNameFieldName);
      formRef?.current.change(isFizFieldName);
      formRef?.current.change(isDocumentOrganizationFieldName);
      formRef?.current.change(idFieldName);
    }
  }, [
    formRef,
    idFieldName,
    innFieldName,
    isDocumentOrganizationFieldName,
    isFizFieldName,
    isOrganizationTemplateNotExist,
    ogrnFieldName,
    organizationNameFieldName,
    organizationTemplateFieldName,
  ]);

  const isShowSonko =
    subjectType?.label === KnmSubjectTypesEnum.UR ||
    subjectType?.label === KnmSubjectTypesEnum.IP ||
    subjectType?.label === KnmSubjectTypesEnum.FIUR;
  const isShowIt = subjectType?.label === KnmSubjectTypesEnum.UR || subjectType?.label === KnmSubjectTypesEnum.FIUR;

  return (
    <Row
      title={
        hideTitle ? undefined : (
          <>
            Сведения о контролируемом лице
            <ErknmFieldMark fieldName={organizationTemplateFieldName} />
          </>
        )
      }
    >
      {kindKnmCode === KnmKindCodesEnum.RO && (
        <Row>
          <FieldBlock
            label={
              <>
                Контролируемое лицо
                <ErknmFieldMark fieldName={organizationTemplateFieldName} />
              </>
            }
            color="black"
            id={organizationTemplateBlockId}
          >
            <SelectInputField
              id={organizationTemplateFieldName}
              name={organizationTemplateFieldName}
              placeholder="Выберите контролируемое лицо"
              size="medium"
              options={organizationsTemplatesSelectOptions}
              disabled={watchOnly}
              onChange={handleOrganizationTemplateSelectChange}
              validate={makeRequired(REQUIRED_ERROR)}
            />
          </FieldBlock>
        </Row>
      )}

      {isShowSubjectInfo && (
        <>
          <ErknmSubjectInfo
            name={`${name}.organization`}
            isWatchOnly={watchOnly || !!ervkId}
            isNewOrganizationTemplate={isNewOrganizationTemplate}
            isExtended={false}
            isAutocomplete={isAutocomplete}
            inn={inn}
            innLabelBlockId={innLabelBlockId}
            ogrn={ogrn}
            ogrnLabelBlockId={ogrnLabelBlockId}
            firstOrganizationName={firstOrganizationName}
            organizationNameBlockId={organizationNameBlockId}
            subjectResult={checkSubjectResult}
            lookupData={lookupData}
            setLookupData={setLookupData}
            subjectType={subjectType}
            kpp={kpp}
            smevRequestStatus={smevRequestStatus}
            rafpNza={rafpNza}
            rafpLabelBlockId={getOrganizationRafpNzaLabelBlockId(id)}
            kppLabelBlockId={getOrganizationKppLabelBlockId(id)}
            snils={snils}
            checkSubjectResult={checkSubjectResult}
            ervkId={ervkId}
            setCheckByInnMessage={!ervkId ? setCheckByInnMessage : undefined}
          />
          {(subjectType?.label === KnmSubjectTypesEnum.UR ||
            subjectType?.label === KnmSubjectTypesEnum.IP ||
            subjectType?.label === KnmSubjectTypesEnum.FIUR) && (
            <KnmOkveds
              name={`${name}.organization.okveds`}
              subjectData={{ inn, ogrn }}
              watchOnly={watchOnly || !!ervkId || !isNewOrganizationTemplate}
              okveds={okveds}
              organizationAutocomplete={isAutocomplete}
              blockId={getOrganizationOkvedsBlockId(id)}
              getOkvedBlockId={getOkvedBlockId}
              checkSubjectResult={checkSubjectResult}
            />
          )}
          {subjectType?.label === KnmSubjectTypesEnum.UR && (
            <KnmSubjectNkoInfo
              id={`${id}.organization`}
              name={`${name}.organization`}
              ogrn={ogrn}
              checkByInnMessage={checkByInnMessage}
              formRef={formRef}
              isShowMessages={true}
              ervkId={ervkId}
            />
          )}
          {isShowIt && (
            <KnmSubjectItInfo
              name={`${name}.organization`}
              inn={inn}
              inspectionResults={undefined}
              isPresentInItRegistry={isPresentInItRegistry}
              itAccreditationResponse={itAccreditationResponse}
              smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
              checkByInnMessage={checkByInnMessage}
              isShowMessages={true}
              ervkId={ervkId}
            />
          )}
          <KnmMspField
            id={`${id}.organization.mspCode`}
            name={mspCodeFieldName}
            value={mspCode}
            checkSubjectResult={checkSubjectResult}
            ervkId={ervkId}
            inn={inn}
            mspRegistryDate={mspRegistryDate}
            formRef={formRef}
            isShowMessages={subjectType?.label !== KnmSubjectTypesEnum.FIZ}
          />
          {isShowSonko && (
            <KnmSonkoField
              inn={inn}
              ogrn={ogrn}
              name={sonkoFieldName}
              subjectType={subjectType}
              value={isSonko}
              id={`${id}.organization.isSonko`}
              checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
              formRef={formRef}
              isShowMessages={true}
            />
          )}
          <Row>
            <ErknmRegistryCheck
              className={styles.RegistryCheckBlock}
              initialCheckSubjectResult={checkSubjectResult}
              isActOrResultDecision
            />
          </Row>
        </>
      )}
    </Row>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectAddStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const ErknmSubjectAdd = connect(mapStateToProps)(ErknmSubjectAddWithoutStore);
